import { useDisclosure } from "@chakra-ui/hooks";
import AtriumApi, { getApiErrorMessage } from "api";
import Badge from "components/badge";
import Button from "components/button";
import Dialog from "components/dialog";
import { useConfirm } from "components/dialog/ConfirmProvider";
import { InputField } from "components/fields";
import { EmptyState, ErrorState } from "components/states";
import notify from "lib/notify";
import { useEffect, useState } from "react";
import { MdAdd, MdVpnKey } from "react-icons/md";
import { formatDate } from "utils/format";
import SettingsSection, { SettingsSectionSkeleton } from "./components/SettingsSection";

const ApiTokenSettings = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [label, setLabel] = useState("");
  const [labelError, setLabelError] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdRaw, setCreatedRaw] = useState(null); // raw atk_live_ token, shown once
  const createDisclosure = useDisclosure();
  const confirm = useConfirm();

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await AtriumApi.get("/v1/integrations/api-tokens");
      setTokens(res?.data?.data || []);
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  };

  const closeCreate = () => {
    setLabelError("");
    createDisclosure.onClose();
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!label.trim()) {
      setLabelError("Label is required");
      return;
    }
    setCreating(true);
    try {
      const res = await AtriumApi.post("/v1/integrations/api-tokens", {
        label: label.trim(),
      });
      setCreatedRaw(res?.data?.data?.rawToken || null); // shown once
      setLabel("");
      closeCreate();
      fetchTokens();
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not create token"));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (t) => {
    const confirmed = await confirm({
      title: `Revoke "${t.label}"?`,
      message: "Apps using this token will lose access immediately. This cannot be undone.",
      confirmLabel: "Revoke",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await AtriumApi.delete(`/v1/integrations/api-tokens/${t.id}`);
      notify.success("Token revoked");
      fetchTokens();
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not revoke token"));
    }
  };

  const copyRaw = () => {
    if (!createdRaw) return;
    navigator.clipboard.writeText(createdRaw);
    notify.success("Copied to clipboard");
  };

  if (loading) return <SettingsSectionSkeleton />;

  const createButton = (
    <Button leftIcon={<MdAdd />} onClick={createDisclosure.onOpen}>
      Create Token
    </Button>
  );

  return (
    <>
      <SettingsSection
        title="API Tokens"
        description="Create an Atrium API token to connect AtriumVerge and other tools."
        action={!loadError && tokens.length > 0 ? createButton : null}
      >
        {loadError ? (
          <ErrorState
            message={getApiErrorMessage(loadError, "Could not load API tokens.")}
            onRetry={fetchTokens}
          />
        ) : tokens.length === 0 ? (
          <EmptyState
            icon={MdVpnKey}
            title="No API Tokens Yet"
            description="Create a token to connect AtriumVerge or another tool to your church's data."
            action={createButton}
          />
        ) : (
          <ul className="divide-y divide-line">
            {tokens.map((t) => (
              <li key={t.id} className="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-fg">{t.label}</p>
                  <p className="mt-0.5 text-xs text-fg-muted">
                    <code>{t.tokenPrefix}…</code> · Created {formatDate(t.createdAt)}
                    {t.lastUsedAt ? ` · Last used ${formatDate(t.lastUsedAt)}` : " · Never used"}
                  </p>
                </div>
                {t.isActive ? (
                  <Button variant="secondary" size="sm" onClick={() => handleRevoke(t)}>
                    Revoke
                  </Button>
                ) : (
                  <Badge tone="neutral">Revoked</Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </SettingsSection>

      <Dialog
        isOpen={createDisclosure.isOpen}
        onClose={closeCreate}
        title="Create API Token"
        description="Tokens are scoped to your church and authenticate the Atrium MCP server and external API."
        footer={
          <>
            <Button variant="secondary" onClick={closeCreate}>
              Cancel
            </Button>
            <Button type="submit" form="create-api-token-form" loading={creating}>
              Create Token
            </Button>
          </>
        }
      >
        <form id="create-api-token-form" onSubmit={handleCreate} noValidate>
          <InputField
            label="Label"
            required
            placeholder="e.g. AtriumVerge"
            value={label}
            error={labelError}
            onChange={(e) => {
              setLabel(e.target.value);
              if (labelError) setLabelError("");
            }}
          />
        </form>
      </Dialog>

      <Dialog
        isOpen={!!createdRaw}
        onClose={() => setCreatedRaw(null)}
        title="Token Created"
        description="Save this token now. It will never be shown again. Paste it into the Connect Your Atrium Account panel in AtriumVerge."
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreatedRaw(null)}>
              Done
            </Button>
            <Button onClick={copyRaw}>Copy to Clipboard</Button>
          </>
        }
      >
        <div className="break-all rounded-control border border-line bg-surface-raised p-3 font-mono text-sm text-fg">
          {createdRaw}
        </div>
      </Dialog>
    </>
  );
};

export default ApiTokenSettings;
